/**
 * Stop a y-websocket / y-partykit provider from sending document writes when
 * the PartyKit connection is read-only.
 *
 * y-partykit already drops inbound SyncStep2/Update on read-only sockets, but
 * the client still emits them: unguarded yTransact paths (camera, project name,
 * track repair) plus IndexedDB-loaded client-only diffs packed into the
 * handshake SyncStep2. Those dropped frames are what ENG-722's Sentry warning
 * is counting. Awareness (cursors / presence) is left alone.
 *
 * Inbound SyncStep2/Update still apply to the real doc so the viewer stays in
 * sync. Only the client's *reply* to SyncStep1 is rewritten to an empty
 * SyncStep2, so local-only diffs are not pushed.
 */

import * as decoding from 'lib0/decoding'
import * as encoding from 'lib0/encoding'
import * as syncProtocol from 'y-protocols/sync'
import * as Y from 'yjs'

const MESSAGE_SYNC = 0

export type SyncMessageHandler = (
  encoder: encoding.Encoder,
  decoder: decoding.Decoder,
  provider: ReadOnlyGuardProvider,
  emitSynced: boolean,
  messageType: number,
) => void

export type ReadOnlyGuardProvider = {
  doc: Y.Doc
  synced: boolean
  disableBc?: boolean
  messageHandlers: unknown[]
  _updateHandler: (update: Uint8Array, origin: unknown) => void
}

const guardedProviders = new WeakSet<object>()
const suppressedProviders = new WeakSet<object>()

export function setProviderDocWritesSuppressed(provider: object, suppressed: boolean): void {
  if (suppressed) suppressedProviders.add(provider)
  else suppressedProviders.delete(provider)
}

export function isProviderDocWritesSuppressed(provider: object): boolean {
  return suppressedProviders.has(provider)
}

/**
 * True when a cached provider was read-only and this boot wants to write.
 * The local Y.Doc may still hold viewer-era diffs; lifting suppression would
 * send them on the next SyncStep2. Discard that cache instead.
 */
export function shouldDiscardSuppressedCollabCache(provider: object | null | undefined, readOnly: boolean): boolean {
  return provider != null && isProviderDocWritesSuppressed(provider) && !readOnly
}

/**
 * Install once per provider. `setProviderDocWritesSuppressed` can turn
 * suppression on. Do not lift it on a reused provider whose Y.Doc may still
 * hold read-only local diffs — discard that cache and boot a fresh session.
 */
export function attachReadOnlyOutboundGuard(provider: ReadOnlyGuardProvider): void {
  if (guardedProviders.has(provider)) return
  guardedProviders.add(provider)

  const originalUpdate = provider._updateHandler
  provider.doc.off('update', originalUpdate)
  const guardedUpdate = (update: Uint8Array, origin: unknown) => {
    if (suppressedProviders.has(provider)) return
    originalUpdate(update, origin)
  }
  provider._updateHandler = guardedUpdate
  provider.doc.on('update', guardedUpdate)

  const originalSync = provider.messageHandlers[MESSAGE_SYNC] as SyncMessageHandler | undefined
  if (!originalSync) return

  provider.messageHandlers[MESSAGE_SYNC] = ((encoder, decoder, syncProvider, emitSynced, messageType) => {
    if (suppressedProviders.has(provider) && decoding.peekVarUint(decoder) === syncProtocol.messageYjsSyncStep1) {
      encoding.writeVarUint(encoder, MESSAGE_SYNC)
      const emptyDoc = new Y.Doc()
      try {
        syncProtocol.readSyncMessage(decoder, encoder, emptyDoc, syncProvider)
      } finally {
        emptyDoc.destroy()
      }
      return
    }
    originalSync(encoder, decoder, syncProvider, emitSynced, messageType)
  }) as SyncMessageHandler
}
